import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AtSign,
  ChevronLeft,
  FileText,
  MessageSquare,
  MessageSquareText,
  Paperclip,
  Send,
  Smile,
  X,
} from "lucide-react";
import { AppLayout } from "../../../components/layout";
import { ContentCard, EmptyState, LoadingState, ui } from "../../../components/ui";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { TablePagination } from "../../../components/ui/TablePagination";
import { cn } from "../../../lib/utils";
import {
  addMessage,
  addReply,
  downloadMessageAttachment,
  downloadReplyAttachment,
  fetchMessages,
  fetchReplies,
  getEmployee,
  getLead,
  getUnreadCount,
  markMessagesRead,
} from "../../../services/leadManagementService";
import { downloadBlob, parseContentDispositionFilename } from "../../../utils/downloadFile";
import sessionDB from "../../../utils/sessionDB";
import { LeadPageHeader, LeadTabs, openLeadTab } from "./LeadDetailPage";
import { ASSIGNMENT_FIELDS } from "../components/AssigningTechnician";
import { leadKey } from "../leadForm";

const K = "leadManagement.messages.";
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.xlsx,.docx";
const MAX_BYTES = 1 * 1024 * 1024;
const MAX_PER_MESSAGE = 5;

/** The emoji palette offered beside the attach button. */
const EMOJIS = [
  "😀", "😂", "😊", "🤔", "😎", "😢", "😡",
  "👍", "👎", "👏", "🙏", "🙌", "💪", "🤝", "👀",
  "🎉", "🔥", "✅", "❌", "⚠️", "⏰", "📌", "🚀",
];

/** Keeps a scrolling list at its newest (bottom) entry once it has loaded. */
const scrollToBottom = (ref) => {
  if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
};

const ROLE_DISPLAY_NAMES = {
  primarySalesExecutiveName: "Primary Sales Executive",
  secondarySalesExecutiveName: "Secondary Sales Executive",
  primaryTechnicianName: "Primary Technician",
  salesSupportExecutiveName: "Sales Support Executive",
  serviceManagerName: "Service Manager",
};

/** Deterministic palette for employee avatars based on string hash */
const AVATAR_PALETTES = [
  "bg-teal-600 text-white",
  "bg-blue-600 text-white",
  "bg-emerald-600 text-white",
  "bg-indigo-600 text-white",
  "bg-violet-600 text-white",
  "bg-amber-600 text-white",
  "bg-rose-600 text-white",
  "bg-cyan-600 text-white",
  "bg-fuchsia-600 text-white",
];

function getAvatarColor(nameOrId) {
  const str = String(nameOrId || "U");
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_PALETTES[Math.abs(hash) % AVATAR_PALETTES.length];
}

function getInitials(name) {
  if (!name) return "U";
  const parts = String(name).trim().split(/\s+/);
  return parts.length >= 2
    ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

const fmtTs = (val) =>
  !val ? "" : new Date(val).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

const fmtFullTs = (val) =>
  !val
    ? ""
    : new Date(val).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

function formatDayLabel(val) {
  if (!val) return "";
  const d = new Date(val);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return "Today";
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const toBase64 = (file) =>
  new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(String(reader.result).split(",")[1]);
    reader.onerror = () => rej(reader.error);
    reader.readAsDataURL(file);
  });

const buildAttachment = async (file) => ({
  fileName: file.name,
  fileByte: await toBase64(file),
  fileSize: file.size,
  contentType: file.name.split(".").pop().toLowerCase(),
});

const downloadAttachment = async (request, info) => {
  const { blob, contentDisposition } = await request();
  downloadBlob({
    blob,
    filename: parseContentDispositionFilename(contentDisposition, info.fileName),
  });
};

function validateFile(file, currentCount, t) {
  if (!file) return null;
  const ext = file.name.split(".").pop().toLowerCase();
  if (!ACCEPT.split(",").includes(`.${ext}`)) return t(`${K}badType`);
  if (file.size > MAX_BYTES) return t(`${K}tooLarge`);
  if (currentCount >= MAX_PER_MESSAGE) return t(`${K}tooManyFiles`);
  return null;
}

function buildMentionData(text, assignees = []) {
  const active = assignees.filter((a) =>
    new RegExp(`@${a.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(\\b|\\s|$)`, "i").test(text)
  );
  const first = active[0] || null;
  const data = {
    mentionedEmployeeId: first?.id ?? null,
    mentionedEmployeeName: first?.name ?? null,
    mentionedEmpId: first?.id ?? null,
    mentionedEmpName: first?.name ?? null,
    mentionedId: first?.id ?? null,
    mentionedName: first?.name ?? null,
    mentionedIds: active.map((m) => m.id).filter(Boolean),
    mentionedNames: active.map((m) => m.name).filter(Boolean),
    mentions: active.map((m) => ({
      id: m.id,
      empId: m.id,
      name: m.name,
      role: m.role,
      key: m.key,
      nameKey: m.nameKey,
    })),
  };
  active.forEach((m) => {
    if (m.key && m.id != null) data[m.key] = m.id;
    if (m.nameKey && m.name) data[m.nameKey] = m.name;
  });
  return data;
}

export function useLeadAssignees(lead) {
  const [assignees, setAssignees] = useState([]);

  useEffect(() => {
    if (!lead) return setAssignees([]);
    const initial = [];
    const missing = [];

    // Lead Creator
    const creatorId = lead.createdBy || lead.creatorId;
    const creatorName = lead.createdName || lead.creatorName || lead.createdByName;
    if (creatorName && String(creatorName).trim()) {
      initial.push({
        id: creatorId != null ? Number(creatorId) : null,
        name: String(creatorName).trim(),
        role: "Lead Creator",
        key: "createdBy",
        nameKey: "createdName",
      });
    } else if (creatorId) {
      missing.push({
        id: String(creatorId),
        key: "createdBy",
        nameKey: "createdName",
        role: "Lead Creator",
      });
    }

    // Lead Assignees
    ASSIGNMENT_FIELDS.forEach(({ key, name, role }) => {
      const empName = lead[name];
      const empId = lead[key];
      const roleLabel = ROLE_DISPLAY_NAMES[name] || role;

      if (empName && String(empName).trim()) {
        initial.push({
          id: empId != null ? Number(empId) : null,
          name: String(empName).trim(),
          role: roleLabel,
          key,
          nameKey: name,
        });
      } else if (empId) {
        missing.push({ id: String(empId), key, nameKey: name, role: roleLabel });
      }
    });

    // Deduplicate by name so a person isn't listed twice if they are creator & assignee
    const unique = [];
    const seenNames = new Set();
    initial.forEach((person) => {
      const lower = person.name.toLowerCase();
      if (!seenNames.has(lower)) {
        seenNames.add(lower);
        unique.push(person);
      }
    });
    setAssignees(unique);

    if (missing.length > 0) {
      Promise.all(
        missing.map((item) =>
          getEmployee(item.id)
            .then((emp) => ({
              id: Number(item.id),
              name: emp.name || emp.employeeName || `Employee ${item.id}`,
              role: item.role,
              key: item.key,
              nameKey: item.nameKey,
            }))
            .catch(() => null)
        )
      ).then((res) => {
        const valid = res.filter(Boolean);
        if (valid.length > 0) {
          setAssignees((prev) => {
            const currentNames = new Set(prev.map((p) => p.name.toLowerCase()));
            const toAdd = valid.filter((v) => !currentNames.has(v.name.toLowerCase()));
            return [...prev, ...toAdd];
          });
        }
      });
    }
  }, [lead]);

  return assignees;
}

/* ------------------------------------------------------------------ UI Components */

function SlackAvatar({ name, size = "md" }) {
  const sz =
    size === "sm"
      ? "w-7 h-7 text-[11px] rounded-lg"
      : "w-8.5 h-8.5 text-xs rounded-xl";

  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center font-bold select-none shadow-2xs",
        sz,
        getAvatarColor(name)
      )}
    >
      {getInitials(name)}
    </div>
  );
}

function SlackAttachmentChip({ info, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={info.fileName}
      className="inline-flex items-center gap-2 rounded-xl border border-(--color-text-border) bg-workspace/70 hover:bg-workspace px-3 py-1.5 text-xs font-semibold text-foreground transition hover:border-primary/50 hover:text-primary cursor-pointer shadow-2xs group"
    >
      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors">
        <FileText className="h-3 w-3" />
      </div>
      <span className="max-w-[160px] truncate">{info.fileName}</span>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
        {info.fileName.split(".").pop()}
      </span>
    </button>
  );
}

function PendingChip({ file, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-primary/40 bg-primary/5 px-2.5 py-1 text-[11px] font-semibold text-foreground">
      <FileText className="h-3.5 w-3.5 text-primary shrink-0" />
      <span className="max-w-[140px] truncate">{file.name}</span>
      <button
        type="button"
        onClick={onRemove}
        className="ml-1 grid h-4 w-4 place-items-center rounded-full hover:bg-rose-100 text-rose-600 transition cursor-pointer"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

function FormattedMessageText({ text, assignees = [] }) {
  if (!text) return null;
  const parts = text.split(/(@[^\s.,!?:;()]+)/g);
  return (
    <span className="[overflow-wrap:anywhere] whitespace-pre-wrap leading-relaxed text-sm">
      {parts.map((part, i) => {
        if (part.startsWith("@")) {
          const raw = part.slice(1).toLowerCase();
          const isA = assignees.some((a) => a.name.toLowerCase() === raw);
          return (
            <span
              key={i}
              className={cn(
                "inline-flex items-center rounded-md px-1.5 py-0.5 text-xs mx-0.5 font-semibold",
                isA
                  ? "bg-primary/15 text-primary border border-primary/25 font-bold"
                  : "bg-primary/10 text-primary"
              )}
            >
              {part}
            </span>
          );
        }
        return part;
      })}
    </span>
  );
}

function DateDivider({ label }) {
  return (
    <div className="relative my-3 flex items-center justify-center">
      <div className="absolute inset-0 flex items-center" aria-hidden="true">
        <div className="w-full border-t border-(--color-text-border)/60" />
      </div>
      <span className="relative rounded-full border border-(--color-text-border)/80 bg-card px-3 py-0.5 text-[11px] font-bold tracking-wide text-muted-foreground shadow-2xs select-none">
        {label}
      </span>
    </div>
  );
}

function UnreadDivider({ unreadCount }) {
  const { t } = useTranslation();
  return (
    <div className="relative my-3 flex items-center justify-center">
      <div className="absolute inset-0 flex items-center" aria-hidden="true">
        <div className="w-full border-t border-emerald-300 dark:border-emerald-800" />
      </div>
      <div className="relative flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-50 px-3.5 py-1 text-xs font-bold text-emerald-700 shadow-xs dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
        </span>
        <span>
          {unreadCount === 1
            ? t(`${K}unreadSingle`, "1 unread message")
            : t(`${K}unreadMultiple`, "{{count}} unread messages", { count: unreadCount })}
        </span>
      </div>
    </div>
  );
}

function SlackComposeBox({
  placeholder,
  value,
  onChange,
  onSend,
  busy,
  onAttach,
  pending,
  onRemove,
  error,
  maxFiles,
  assignees = [],
  compact = false,
}) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);

  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const [emojiOpen, setEmojiOpen] = useState(false);

  /** Puts the emoji where the cursor is (replacing any selection) and keeps typing there. */
  const insertEmoji = (emoji) => {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    onChange(`${value.slice(0, start)}${emoji}${value.slice(end)}`);
    setEmojiOpen(false);
    setTimeout(() => {
      el?.focus();
      el?.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  };

  const checkMentionTrigger = (text, cursorPos) => {
    const match = text.slice(0, cursorPos).match(/(?:^|\s)@([^\s@]*)$/);
    if (match && assignees.length > 0) {
      setMentionQuery(match[1]);
      setShowMentions(true);
      setMentionIndex(0);
    } else {
      setShowMentions(false);
    }
  };

  const handleChange = (e) => {
    onChange(e.target.value);
    checkMentionTrigger(e.target.value, e.target.selectionStart);
  };

  const filteredAssignees = assignees.filter(
    (a) =>
      a.name.toLowerCase().includes(mentionQuery.toLowerCase()) ||
      a.role.toLowerCase().includes(mentionQuery.toLowerCase())
  );

  const selectMention = (assignee) => {
    const cursor = textareaRef.current?.selectionStart ?? value.length;
    const beforeAt = value.slice(0, value.slice(0, cursor).lastIndexOf("@"));
    const after = value.slice(cursor);
    const inserted = `@${assignee.name} `;
    onChange(`${beforeAt}${inserted}${after}`);
    setShowMentions(false);

    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const p = beforeAt.length + inserted.length;
        textareaRef.current.setSelectionRange(p, p);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (showMentions && filteredAssignees.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        return setMentionIndex((prev) => (prev + 1) % filteredAssignees.length);
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        return setMentionIndex(
          (prev) => (prev - 1 + filteredAssignees.length) % filteredAssignees.length
        );
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        return selectMention(filteredAssignees[mentionIndex]);
      }
      if (e.key === "Escape") {
        e.preventDefault();
        return setShowMentions(false);
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowMentions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative">
      {showMentions && filteredAssignees.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute bottom-full left-0 mb-2 w-72 max-w-full overflow-hidden rounded-xl border border-(--color-text-border)/80 bg-card shadow-xl ring-1 ring-black/5 z-50 animate-in fade-in slide-in-from-bottom-2 duration-150"
        >
          <div className="border-b border-(--color-text-border)/40 bg-workspace/60 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center justify-between">
            <span className="flex items-center gap-1">
              <AtSign className="h-3 w-3 text-primary" />
              <span>{t(`${K}mentionAssignee`, "Mention Assignee")}</span>
            </span>
            <span className="text-[10px] font-normal lowercase text-muted-foreground/75">↑↓ & enter</span>
          </div>
          <div className="max-h-52 overflow-y-auto p-1">
            {filteredAssignees.map((emp, idx) => (
              <button
                key={emp.nameKey || emp.id || idx}
                type="button"
                onClick={() => selectMention(emp)}
                onMouseEnter={() => setMentionIndex(idx)}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-xs transition cursor-pointer",
                  idx === mentionIndex
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-foreground hover:bg-workspace"
                )}
              >
                <SlackAvatar name={emp.name} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-foreground">{emp.name}</div>
                  {/* Display only: an assignee shows its field's label; the role sent with a mention is unchanged. */}
                  <div className="truncate text-[10px] text-muted-foreground">
                    {t(leadKey("fields", emp.key), { defaultValue: emp.role })}
                  </div>
                </div>
                <span className="text-[11px] text-primary/70 font-bold">@</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div
        className={cn(
          "rounded-2xl border transition-all duration-150 bg-white shadow-xs focus-within:ring-2 focus-within:ring-primary/15 focus-within:border-primary",
          error ? "border-rose-400" : "border-(--color-text-border)"
        )}
      >
        <textarea
          ref={textareaRef}
          rows={compact ? 2 : 3}
          value={value}
          disabled={busy}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full resize-none bg-transparent px-3.5 pt-3 pb-2 text-sm text-foreground outline-none placeholder:text-muted-foreground/60 disabled:opacity-50 min-h-[48px] max-h-[140px]"
        />

        {pending.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-3 pb-2">
            {pending.map((f, i) => (
              <PendingChip key={i} file={f} onRemove={() => onRemove(i)} />
            ))}
          </div>
        )}

        <div className="flex items-center justify-between border-t border-(--color-text-border)/40 bg-slate-50/60 px-3 py-2 rounded-b-2xl">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={busy || assignees.length === 0}
              onClick={() => {
                const cursor = textareaRef.current?.selectionStart ?? value.length;
                const before = value.slice(0, cursor);
                const after = value.slice(cursor);
                const prefix =
                  before.length > 0 && !before.endsWith(" ") && !before.endsWith("\n") ? " @" : "@";
                onChange(`${before}${prefix}${after}`);
                setMentionQuery("");
                setShowMentions(true);
                setMentionIndex(0);
                setTimeout(() => {
                  textareaRef.current?.focus();
                  const p = before.length + prefix.length;
                  textareaRef.current?.setSelectionRange(p, p);
                }, 0);
              }}
              title={t(`${K}mention`, "Mention team member (@)")}
              className="grid h-7 w-7 place-items-center rounded-lg border border-(--color-text-border)/60 text-muted-foreground hover:text-primary hover:border-primary/50 hover:bg-white transition disabled:opacity-40 cursor-pointer"
            >
              <AtSign className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              disabled={busy || pending.length >= maxFiles}
              onClick={() => fileRef.current?.click()}
              title={t(`${K}attach`, "Attach file")}
              className="grid h-7 w-7 place-items-center rounded-lg border border-(--color-text-border)/60 text-muted-foreground hover:text-primary hover:border-primary/50 hover:bg-white transition disabled:opacity-40 cursor-pointer"
            >
              <Paperclip className="h-3.5 w-3.5" />
            </button>
            <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  disabled={busy}
                  title={t(`${K}emoji`)}
                  className="grid h-7 w-7 place-items-center rounded-lg border border-(--color-text-border)/60 text-muted-foreground hover:text-primary hover:border-primary/50 hover:bg-white transition disabled:opacity-40 cursor-pointer"
                >
                  <Smile className="h-3.5 w-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                side="top"
                onCloseAutoFocus={(event) => event.preventDefault()}
                className="grid w-auto grid-cols-8 gap-0.5 rounded-xl border-(--color-text-border) bg-white p-1.5"
              >
                {EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => insertEmoji(emoji)}
                    className="grid h-8 w-8 cursor-pointer place-items-center rounded-lg text-lg transition hover:bg-workspace"
                  >
                    {emoji}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          </div>

          <button
            type="button"
            disabled={busy || !value.trim()}
            onClick={onSend}
            className={cn(
              ui.primaryBtn,
              "h-8 px-3 rounded-lg text-xs font-semibold gap-1.5 shadow-xs cursor-pointer"
            )}
            title={t(`${K}send`, "Send")}
          >
            <span>{t(`${K}send`, "Send")}</span>
            <Send className="h-3 w-3" />
          </button>
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          onAttach(e.target.files[0]);
          e.target.value = "";
        }}
      />
      {error && <p className="mt-1 text-[11px] font-semibold text-rose-600">{error}</p>}
    </div>
  );
}

/** Slack Message Row: clicking anywhere opens its reply thread (both desktop & mobile) */
function SlackMessageRow({ leadId, message, assignees, isUnread, isActiveThread, onOpenThread }) {
  const { t } = useTranslation();
  const author = message.createdName ?? message.createdBy;
  const replyCount = message.replyCount ?? 0;

  return (
    <div
      onClick={() => onOpenThread(message)}
      className={cn(
        "group relative flex gap-3 px-3.5 py-3 transition-all rounded-xl cursor-pointer select-text",
        isActiveThread
          ? "bg-primary/[0.06] ring-1 ring-primary/25 shadow-2xs"
          : isUnread
            ? "bg-emerald-50/50 hover:bg-emerald-50/80 border-l-2 border-emerald-500"
            : "hover:bg-slate-50/80"
      )}
    >
      <div className="absolute right-3 -top-2.5 z-10 flex items-center rounded-lg border border-(--color-text-border) bg-white p-0.5 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpenThread(message);
          }}
          className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold text-muted-foreground hover:bg-workspace hover:text-primary transition cursor-pointer"
        >
          <MessageSquareText className="h-3.5 w-3.5 text-primary" />
          <span>{t(`${K}reply`, "Reply")}</span>
        </button>
      </div>

      <SlackAvatar name={author} />

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[13px] font-bold text-foreground">{author}</span>
          <span className="text-[11px] per-file-hint font-normal" title={fmtFullTs(message.createdAt)}>
            {fmtTs(message.createdAt)}
          </span>
          {isUnread && (
            <span className="rounded-full bg-emerald-100 px-2 py-0.2 text-[10px] font-bold text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
              New
            </span>
          )}
        </div>

        <div className="mt-1 text-sm text-foreground">
          <FormattedMessageText text={message.messageText} assignees={assignees} />
        </div>

        {message.attachments?.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
            {message.attachments.map((a) => (
              <SlackAttachmentChip
                key={a.index}
                info={a}
                onClick={() =>
                  downloadAttachment(() => downloadMessageAttachment(leadId, message.messageId, a.index), a)
                }
              />
            ))}
          </div>
        )}

        {replyCount > 0 && (
          <div className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg border border-(--color-text-border)/70 bg-white hover:bg-primary/5 px-2.5 py-1 text-xs font-semibold text-primary transition shadow-2xs">
            <MessageSquare className="h-3.5 w-3.5 text-primary" />
            <span>
              {replyCount === 1
                ? t(`${K}replySingle`, "1 reply")
                : t(`${K}replies`, "{{count}} replies", { count: replyCount })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function SlackReplyRow({ leadId, reply, assignees }) {
  const author = reply.createdName ?? reply.createdBy;

  return (
    <div className="flex gap-3 p-3 rounded-xl hover:bg-white/80 transition-colors">
      <SlackAvatar name={author} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-xs font-bold text-foreground">{author}</span>
          <span className="text-[11px] per-file-hint font-normal" title={fmtFullTs(reply.createdAt)}>
            {fmtTs(reply.createdAt)}
          </span>
        </div>
        <div className="mt-1 text-sm text-foreground">
          <FormattedMessageText text={reply.replyText} assignees={assignees} />
        </div>
        {reply.attachments?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {reply.attachments.map((a) => (
              <SlackAttachmentChip
                key={a.index}
                info={a}
                onClick={() => downloadAttachment(() => downloadReplyAttachment(leadId, reply.replyId, a.index), a)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Right Column: Slack Thread Panel with date dividers same as messages */
function SlackThreadPanel({ leadId, activeMessage, onClose, assignees, onReplyAdded, onBackToMessages }) {
  const { t } = useTranslation();
  const [replies, setReplies] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [text, setText] = useState("");
  const [pending, setPending] = useState([]);
  const [fileError, setFileError] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");

  const repliesRef = useRef(null);

  // Opens on the newest reply rather than the oldest.
  useEffect(() => {
    if (!loading) scrollToBottom(repliesRef);
  }, [loading, replies]);

  const loadReplies = useCallback(() => {
    if (!activeMessage) return setReplies(null);
    setLoading(true);
    setError("");
    fetchReplies(leadId, activeMessage.messageId)
      .then(setReplies)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [leadId, activeMessage]);

  useEffect(() => {
    loadReplies();
  }, [loadReplies]);

  const attach = (file) => {
    const err = validateFile(file, pending.length, t);
    if (err) return setFileError(err);
    setFileError("");
    setPending((c) => [...c, file]);
  };

  const send = async () => {
    if (!text.trim() || !activeMessage) return;
    setSending(true);
    setSendError("");
    try {
      const atts = await Promise.all(pending.map(buildAttachment));
      const mentionData = buildMentionData(text, assignees);
      await addReply(leadId, activeMessage.messageId, text.trim(), atts, mentionData);
      setText("");
      setPending([]);
      onReplyAdded?.(activeMessage.messageId);
      loadReplies();
    } catch (e) {
      setSendError(e.message);
    } finally {
      setSending(false);
    }
  };

  if (!activeMessage) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8 text-center bg-slate-50/50">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-xs">
          <MessageSquareText className="h-7 w-7" />
        </div>
        <h3 className="mt-4 text-base font-bold text-foreground">
          {t(`${K}threadReplies`, "Replies")}
        </h3>
        <p className="mt-1.5 max-w-xs text-xs per-file-hint leading-relaxed">
          {t(`${K}noThreadSelectedHint`, "Select any message to view its replies")}
        </p>
      </div>
    );
  }

  const parentAuthor = activeMessage.createdName ?? activeMessage.createdBy;

  return (
    <div className="flex h-full flex-col bg-slate-50/60">
      {/* Header without count badge */}
      <div className="flex items-center justify-between border-b border-(--color-text-border)/60 bg-white px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={onBackToMessages}
            className="lg:hidden grid h-8 w-8 place-items-center rounded-lg border border-(--color-text-border)/60 text-muted-foreground hover:bg-workspace hover:text-foreground transition cursor-pointer"
            title="Back to messages"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-foreground">
              {t(`${K}threadReplies`, "Replies")}
            </h3>
            <p className="truncate text-xs per-file-hint">
              {t(`${K}activeThreadWith`, { name: parentAuthor, defaultValue: `with ${parentAuthor}` })}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          title={t(`${K}closeThread`, "Close thread")}
          className="grid h-8 w-8 place-items-center rounded-lg border border-(--color-text-border)/60 text-muted-foreground hover:bg-workspace hover:text-foreground transition cursor-pointer"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Replies Stream with Date Dividers */}
      <div ref={repliesRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Pinned Parent Message Card */}
        <div className="rounded-xl border border-(--color-text-border) bg-white p-3.5 shadow-2xs">
          <div className="mb-2 flex items-center justify-between gap-2 border-b border-(--color-text-border)/40 pb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              {t(`${K}parentMessage`, "Original Message")}
            </span>
            <span className="text-[11px] per-file-hint" title={fmtFullTs(activeMessage.createdAt)}>
              {fmtTs(activeMessage.createdAt)}
            </span>
          </div>
          <div className="flex gap-2.5">
            <SlackAvatar name={parentAuthor} size="sm" />
            <div className="min-w-0 flex-1">
              <span className="text-xs font-bold text-foreground">{parentAuthor}</span>
              <div className="mt-1 text-sm text-foreground">
                <FormattedMessageText text={activeMessage.messageText} assignees={assignees} />
              </div>
              {activeMessage.attachments?.length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {activeMessage.attachments.map((a) => (
                    <SlackAttachmentChip
                      key={a.index}
                      info={a}
                      onClick={() =>
                        downloadAttachment(
                          () => downloadMessageAttachment(leadId, activeMessage.messageId, a.index),
                          a
                        )
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Replies List */}
        {loading ? (
          <div className="py-8">
            <LoadingState message={t(`${K}loadingReplies`, "Loading replies...")} />
          </div>
        ) : error ? (
          <p className="text-xs font-semibold text-rose-600">{error}</p>
        ) : replies && replies.length === 0 ? (
          <div className="py-6 text-center text-xs per-file-hint">
            {t(`${K}noReplies`, "No replies yet.")}
          </div>
        ) : (
          <div className="space-y-1">
            {replies?.map((reply, index) => {
              const currDate = formatDayLabel(reply.createdAt);
              const prevDate = index > 0 ? formatDayLabel(replies[index - 1].createdAt) : null;
              const showDateDivider = currDate && currDate !== prevDate;

              return (
                <div key={reply.replyId}>
                  {showDateDivider && <DateDivider label={currDate} />}
                  <SlackReplyRow leadId={leadId} reply={reply} assignees={assignees} />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="border-t border-(--color-text-border)/60 bg-white p-3">
        <SlackComposeBox
          placeholder={t(`${K}replyPlaceholder`, "Write a reply...")}
          value={text}
          onChange={setText}
          onSend={send}
          busy={sending}
          onAttach={attach}
          pending={pending}
          onRemove={(i) => setPending((c) => c.filter((_, idx) => idx !== i))}
          error={fileError || sendError}
          maxFiles={MAX_PER_MESSAGE}
          assignees={assignees}
          compact
        />
      </div>
    </div>
  );
}

/* ----------------------------------------------------------- Main Messages Workspace */

function MessagesContent({ lead, onUnreadCountChange }) {
  const { t } = useTranslation();
  const { leadId } = lead;
  const assignees = useLeadAssignees(lead);

  const [messages, setMessages] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [lastReadMessageId, setLastReadMessageId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [markedReadSession, setMarkedReadSession] = useState(false);

  const [activeThreadMessage, setActiveThreadMessage] = useState(null);
  const [mobileTab, setMobileTab] = useState("messages");

  const [text, setText] = useState("");
  const [pending, setPending] = useState([]);
  const [fileError, setFileError] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");

  const messagesRef = useRef(null);

  // Each load (open, page change, after sending) lands on the newest message.
  useEffect(() => {
    if (!loading) scrollToBottom(messagesRef);
  }, [loading]);

  const load = useCallback(
    async (p = 0) => {
      setLoading(true);
      setLoadError("");
      try {
        const [data, unreadDTO] = await Promise.all([
          fetchMessages(leadId, p),
          getUnreadCount(leadId).catch(() => null),
        ]);

        const rawList = data?.content ?? [];
        setMessages(rawList.slice().reverse());
        setTotalPages(data?.totalPages ?? 1);
        setTotalElements(data?.totalElements ?? rawList.length);
        setPage(p);

        const lastRead =
          data?.lastReadMessageId ??
          data?.lastMessageReadId ??
          unreadDTO?.lastReadMessageId ??
          unreadDTO?.lastMessageReadId ??
          null;

        const currentEmpId = Number(sessionDB.getEmpId());
        let count = 0;
        if (lastRead != null && Number(lastRead) > 0) {
          count = rawList.filter(
            (m) => m.messageId > Number(lastRead) && Number(m.createdBy) !== currentEmpId
          ).length;
        } else if (unreadDTO?.unreadCount != null) {
          count = Number(unreadDTO.unreadCount);
        } else if (data?.unreadCount != null) {
          count = Number(data.unreadCount);
        }

        if (p === 0 && !markedReadSession) {
          setLastReadMessageId(lastRead);
          setUnreadCount(count);
          onUnreadCountChange?.(count);

          const newestId = rawList[0]?.messageId;
          if (newestId && count > 0) {
            markMessagesRead(leadId, newestId).catch(() => { });
            setMarkedReadSession(true);
          }
        }
      } catch (e) {
        setLoadError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [leadId, markedReadSession, onUnreadCountChange]
  );

  useEffect(() => {
    load(0);
  }, [load]);

  const attach = (file) => {
    const err = validateFile(file, pending.length, t);
    if (err) return setFileError(err);
    setFileError("");
    setPending((c) => [...c, file]);
  };

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    setSendError("");
    try {
      const atts = await Promise.all(pending.map(buildAttachment));
      const mentionData = buildMentionData(text, assignees);
      await addMessage(leadId, text.trim(), atts, mentionData);
      setText("");
      setPending([]);
      setUnreadCount(0);
      onUnreadCountChange?.(0);
      await load(0);
    } catch (e) {
      setSendError(e.message);
    } finally {
      setSending(false);
    }
  };

  const isMessageUnread = (msg) => {
    const currentEmpId = Number(sessionDB.getEmpId());
    if (Number(msg.createdBy) === currentEmpId) return false;
    if (lastReadMessageId != null && Number(lastReadMessageId) > 0) {
      return msg.messageId > Number(lastReadMessageId);
    }
    if (unreadCount > 0) {
      const others = messages.filter((m) => Number(m.createdBy) !== currentEmpId);
      return others.slice(-unreadCount).map((m) => m.messageId).includes(msg.messageId);
    }
    return false;
  };

  const handleOpenThread = (msg) => {
    setActiveThreadMessage(msg);
    setMobileTab("replies");
  };

  const handleCloseThread = () => {
    setActiveThreadMessage(null);
    setMobileTab("messages");
  };

  const handleReplyAdded = (parentMessageId) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.messageId === parentMessageId ? { ...m, replyCount: (m.replyCount || 0) + 1 } : m
      )
    );
  };

  return (
    <div className="rounded-2xl border border-(--color-text-border) bg-white shadow-sm overflow-hidden flex flex-col h-[740px] max-h-[calc(100vh-210px)] min-h-[580px]">
      {/* Main Split Layout */}
      <div className="flex flex-1 min-h-0 min-w-0">
        {/* Left Panel: Messages Stream */}
        <div
          className={cn(
            "flex flex-1 min-w-0 flex-col h-full bg-white",
            mobileTab === "replies" ? "hidden lg:flex" : "flex"
          )}
        >

          {unreadCount > 0 && (
            <div className="flex items-center gap-2.5 border-b border-emerald-200 bg-emerald-50/80 px-4 py-2 text-xs text-emerald-800">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              <span className="font-semibold text-xs">
                {unreadCount === 1
                  ? t(`${K}unreadSingle`, "1 unread message")
                  : t(`${K}unreadMultiple`, "{{count}} unread messages", { count: unreadCount })}
              </span>
            </div>
          )}

          <div ref={messagesRef} className="flex-1 overflow-y-auto p-4 space-y-1">
            {loading ? (
              <div className="py-12">
                <LoadingState message={t(`${K}loading`)} />
              </div>
            ) : loadError ? (
              <p className="text-xs font-semibold text-rose-600 p-4">{loadError}</p>
            ) : messages.length === 0 ? (
              <div className="py-16">
                <EmptyState icon={MessageSquare} title={t(`${K}empty`)} message={t(`${K}emptyHint`)} />
              </div>
            ) : (
              messages.map((msg, index) => {
                const isUnread = isMessageUnread(msg);
                const isFirstUnread = isUnread && (index === 0 || !isMessageUnread(messages[index - 1]));
                const currDate = formatDayLabel(msg.createdAt);
                const prevDate = index > 0 ? formatDayLabel(messages[index - 1].createdAt) : null;
                const showDateDivider = currDate && currDate !== prevDate;

                return (
                  <div key={msg.messageId}>
                    {showDateDivider && <DateDivider label={currDate} />}
                    {isFirstUnread && unreadCount > 0 && (
                      <UnreadDivider unreadCount={unreadCount} />
                    )}
                    <SlackMessageRow
                      leadId={leadId}
                      message={msg}
                      assignees={assignees}
                      isUnread={isUnread}
                      isActiveThread={activeThreadMessage?.messageId === msg.messageId}
                      onOpenThread={handleOpenThread}
                    />
                  </div>
                );
              })
            )}
          </div>

          <div className="border-t border-(--color-text-border)/60 bg-white p-3.5">
            <SlackComposeBox
              placeholder={t(`${K}placeholder`, "Write a message..")}
              value={text}
              onChange={setText}
              onSend={send}
              busy={sending}
              onAttach={attach}
              pending={pending}
              onRemove={(i) => setPending((c) => c.filter((_, idx) => idx !== i))}
              error={fileError || sendError}
              maxFiles={MAX_PER_MESSAGE}
              assignees={assignees}
            />
          </div>
        </div>

        {/* Right Panel: Replies Stream */}
        <div
          className={cn(
            "w-full lg:w-[420px] xl:w-[480px] shrink-0 border-l border-(--color-text-border)/60 flex flex-col h-full bg-slate-50/50",
            mobileTab === "messages" ? "hidden lg:flex" : "flex"
          )}
        >
          <SlackThreadPanel
            leadId={leadId}
            activeMessage={activeThreadMessage}
            onClose={handleCloseThread}
            assignees={assignees}
            onReplyAdded={handleReplyAdded}
            onBackToMessages={() => setMobileTab("messages")}
          />
        </div>
      </div>

      {/* Pagination at the end of the card like the screenshot */}
      {!loading && totalPages > 1 && (
        <div className={cn("shrink-0 bg-white", mobileTab === "replies" && "hidden lg:block")}>
          <TablePagination
            page={page + 1}
            totalPages={totalPages}
            totalItems={totalElements}
            pageSize={20}
            onPageChange={(next) => load(next - 1)}
          />
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Page Shell */

export function MessagesPage() {
  const { t } = useTranslation();
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    getLead(leadId)
      .then(setLead)
      .catch((e) => setLoadError(e.message));
  }, [leadId]);

  return (
    <AppLayout className="space-y-5">
      {lead ? <LeadPageHeader lead={lead} /> : null}
      {loadError ? <p className="text-[12px] font-semibold text-rose-600">{loadError}</p> : null}

      <LeadTabs
        active="messages"
        unreadCount={unreadCount}
        onChange={(tab) => tab !== "messages" && openLeadTab(navigate, leadId, tab, lead)}
      />

      {lead ? (
        <MessagesContent lead={lead} onUnreadCountChange={setUnreadCount} />
      ) : (
        <ContentCard>
          <LoadingState message={t(`${K}loading`)} />
        </ContentCard>
      )}
    </AppLayout>
  );
}

export default MessagesPage;
